import { Download } from 'lucide-react'
import FileField from './FileField'
import { InfoNote } from './Form'
import './ExcelUploadFields.css'

/**
 * The excel-upload body the legacy repeats verbatim in several modals: the format
 * line, the required-columns table, the notes block with the template link, and the
 * file input.
 *
 * Strings are passed in rather than baked in, because the wording differs slightly
 * between callers — the group bulk update accepts .xlsx only, the user import also
 * takes .csv.
 *
 * @param {{name: string, required: boolean}[]} columns
 */
export default function ExcelUploadFields({
  id,
  name = 'excelFile',
  accept,
  columns,
  templateHref,
  error,
  onChange,
  strings,
}) {
  return (
    <>
      <p className="excel-upload__line">
        <bdi>{strings.intro}</bdi>
      </p>

      {/* Not every import states its columns. The questions upload just points
          at a template and takes the file back, so the spec card is skipped
          rather than rendered as an empty table. */}
      {columns?.length > 0 && (
      <section className="excel-upload__columns">
        {/* The template belongs beside the spec it implements, not buried at the
            bottom of a notes block — "these are the columns" and "here is a file
            that already has them" are one thought. */}
        <header className="excel-upload__columns-head">
          <h4 className="excel-upload__columns-title">
            <bdi>{strings.columnsIntro}</bdi>
          </h4>
          <a className="excel-upload__template" href={templateHref} download>
            <Download size={14} strokeWidth={1.75} aria-hidden="true" />
            <bdi>{strings.template}</bdi>
          </a>
        </header>

        <div className="excel-upload__scroll">
          <table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.name} scope="col">
                    {/* `dir` sits on a SPAN, not on the cell. On the cell it also
                        decides where the text sits, so every header parked
                        itself against its cell's left edge while the value
                        underneath sat against the right — one column, two
                        opposite edges, up to 270px apart. The span keeps the
                        Latin ordering and leaves the alignment to the sheet. */}
                    <span dir="ltr">{column.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {columns.map((column) => (
                  <td
                    key={column.name}
                    className={column.required ? 'is-required' : undefined}
                  >
                    <bdi>{column.required ? strings.mandatory : strings.optional}</bdi>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      )}

      {!columns?.length && templateHref && (
        <p className="excel-upload__standalone-template">
          <a className="excel-upload__template" href={templateHref} download>
            <Download size={14} strokeWidth={1.75} aria-hidden="true" />
            <bdi>{strings.template}</bdi>
          </a>
        </p>
      )}

      <InfoNote lead={strings.notesTitle}>
        <bdi>{strings.notes}</bdi>
      </InfoNote>

      <div>
        <FileField id={id} name={name} accept={accept} onChange={onChange} />
        {error && (
          <p className="form-row__error" role="alert">
            <bdi>{error}</bdi>
          </p>
        )}
      </div>
    </>
  )
}
